import { useQuery, useMutation, UseQueryOptions } from '@tanstack/react-query'
import { ModelsFilterParams, CreateModelParams, UpdateModelParams, ModelsListResponse } from '@/types/model'
import { useModelsStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useModels = (
  params: ModelsFilterParams,
  options?: Omit<UseQueryOptions<ModelsListResponse>, 'queryKey' | 'queryFn'>
) => {
  const fetchModels = useModelsStore(state => state.fetchModels)

  return useQuery({
    queryKey: ['models', params],
    queryFn: () => fetchModels(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options
  })
}

export const useModel = (id: string) => {
  const getModel = useModelsStore(state => state.getModel)

  return useQuery({
    queryKey: ['model', id],
    queryFn: () => getModel(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateModel = () => {
  const createModel = useModelsStore(state => state.createModel)

  return useMutation({
    mutationFn: (params: CreateModelParams) => createModel(params),
    onSuccess: () => {}
  })
}

export const useUpdateModel = () => {
  const updateModel = useModelsStore(state => state.updateModel)

  return useMutation({
    mutationFn: ({ modelId, params }: { modelId: string; params: UpdateModelParams }) => updateModel(modelId, params),
    onSuccess: () => {}
  })
}

export const useDeleteModel = () => {
  const deleteModel = useModelsStore(state => state.deleteModel)

  return useMutation({
    mutationFn: (id: string) => deleteModel(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteModels = () => {
  const bulkDeleteModels = useModelsStore(state => state.bulkDeleteModels)
  const deselectAllModels = useModelsStore(state => state.deselectAllModels)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteModels(ids),
    onSuccess: response => {
      deselectAllModels()
      handleBulkDeleteSuccess(response)
    }
  })
}
